/**
 * SDK v2 entry. IIFE bundle that installs window.emblue and drains the stub
 * queue created by the bootstrap (spec §0).
 */
import type { Api } from './core/api';
import type { QueuedCall, SdkConfig, SdkConfigInput } from './core/types';
import { defaultConfig, resolveConfig } from './core/config';
import { Consent } from './core/consent';
import { Identity } from './core/identity';
import { Optin } from './core/optin';
import { Queue } from './core/queue';
import { readCachedConfig } from './core/remote-config';
import { buildApi, fireReady } from './core/api';
import { log, setDebug } from './core/log';
import { installPageview } from './auto-tracking/pageview';
import { installClicks } from './auto-tracking/clicks';
import { installForms } from './auto-tracking/forms';
import { installScroll } from './auto-tracking/scroll';
import { installEngagement } from './auto-tracking/engagement';
import { installEmailCapture } from './auto-tracking/email-capture';
import { installDataLayer } from './auto-tracking/datalayer';
import { installHeatmap } from './auto-tracking/heatmap';
import { installVtex } from './adapters/vtex';
import { installShopify } from './adapters/shopify';
import { installTiendanube } from './adapters/tiendanube';

interface StubQueue {
	q?: QueuedCall[];
}

/** `init` accepts either the account write key as a string
 *  (`emblue('init', 'wk_...')`) or a full config object. The snippet served by
 *  `GET /snippet` uses the object form and includes the account's level. */
function normalizeInitArg(arg: unknown): SdkConfigInput | undefined {
	if (typeof arg === 'string') return { writeKey: arg };
	if (arg && typeof arg === 'object') return arg as SdkConfigInput;
	return undefined;
}

(function main() {
	if ((window as any).__emblueV2Loaded) {
		log('SDK already loaded, skipping');
		return;
	}
	(window as any).__emblueV2Loaded = true;

	// 1. Capture any calls made before the SDK loaded (queued by bootstrap).
	const existing = (window as any).emblue as Api | StubQueue | undefined;
	const queuedCalls: QueuedCall[] = (existing && (existing as StubQueue).q) || [];

	// 2. Build everything with defaults; init() merge happens later if called.
	let config: SdkConfig = defaultConfig();

	let initialConfig: SdkConfigInput | undefined;
	const initCallIdx = queuedCalls.findIndex((c) => c[0] === 'init');
	if (initCallIdx !== -1) {
		const initCall = queuedCalls.splice(initCallIdx, 1)[0];
		initialConfig = normalizeInitArg(initCall[1]);
		// resolveConfig, not mergeConfig: the level in the snippet has to expand
		// into its auto-tracking preset before anything is installed below.
		config = resolveConfig(config, initialConfig);
	}

	// The account's level as the worker last reported it, cached per tab. Applied
	// here — after the snippet's init, before Consent and before any install*() —
	// so the auto-trackers wire up at the account's *current* level even when the
	// pasted snippet is stale. resolveConfig, not mergeConfig: the level has to
	// expand into its preset. Note this means a cached level beats an explicit
	// local `autoTrack` override, which is intended (see resolveConfig's docstring).
	const cachedConfig = readCachedConfig(config.writeKey);
	if (cachedConfig) {
		config = resolveConfig(config, { level: cachedConfig.level });
	}

	setDebug(config.debug);
	if (cachedConfig) log('applied cached account level', cachedConfig.level);

	const consent = new Consent(config);
	consent.init();

	const identity = new Identity(config, consent);
	identity.init();

	// The level the worker reports is applied in memory as well as cached. In-memory
	// only reaches flags that are read at emit time (`outbound`, `downloads` — see
	// clicks.ts); everything else is latched when install*() ran above. Its main job
	// is populating the cache that the next page load in this tab reads.
	const queue = new Queue(config, identity, consent, (level) => {
		if (level === config.level) return;
		Object.assign(config, resolveConfig(config, { level }));
		log('account level applied in memory', level);
	});

	// Reads its dedup ledger before anything can capture an identifier, so a
	// contact opted in on a previous page load isn't announced twice.
	const optin = new Optin(config, identity, consent, queue);
	optin.init();

	const api: Api = buildApi(config, identity, consent, queue, optin);
	api.init = (cfg?: string | SdkConfigInput) => {
		// init() after load is mostly a no-op (the SDK is already running) —
		// merge config changes that don't need teardown. A level change here
		// updates the flags but does not re-install the auto-trackers, which are
		// already bound; it takes effect on the next page load.
		const normalized = normalizeInitArg(cfg);
		if (normalized) {
			Object.assign(config, resolveConfig(config, normalized));
			setDebug(config.debug);
		}
	};

	// 3. session.start fires once per new session (no prior cookie). Has to
	//    happen before auto-tracking so it lands ahead of the initial page_view.
	//    Skipped under coexistWithLegacy — the legacy SDK owns the session
	//    signal in that mode (same rule as page_view, see installPageview).
	if (identity.sessionIsNew && !config.coexistWithLegacy) {
		api.track('session.start', {
			referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
			entry_url: typeof location !== 'undefined' ? location.href : undefined,
		});
	}

	// 4. Auto-tracking modules.
	installPageview(config, api);
	installClicks(config, api);
	installForms(config, api);
	installScroll(config, api);
	installEngagement(config, api);
	installEmailCapture(config, identity, optin);
	installDataLayer(config, identity, optin);
	installHeatmap(config, api);

	// 5. Platform adapters (auto-detected).
	installVtex(api);
	installShopify(api);
	installTiendanube(api);

	// 6. Expose API + drain stub queue.
	(window as any).emblue = api;
	for (const call of queuedCalls) {
		const [method, ...rest] = call;
		try {
			(api as any)(method, ...rest);
		} catch (e) {
			log('drain error for', method, e);
		}
	}

	fireReady();
	log('SDK ready', { version: config.sdkVersion, coexistWithLegacy: config.coexistWithLegacy });
})();
